import { useState, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { speakingAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const SAMPLE_PAIRS = [
  {
    id: 1,
    label: 'City vs. Countryside',
    imageA: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&q=80',
    imageB: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=600&q=80',
    prompt: 'Compare these two images. Describe what you see in each picture and discuss the similarities and differences between city life and countryside life.',
  },
  {
    id: 2,
    label: 'Traditional vs. Modern',
    imageA: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=600&q=80',
    imageB: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&q=80',
    prompt: 'Look at both images carefully. Compare the traditional and modern architectural styles shown, and discuss which environment you would prefer to live or work in.',
  },
]

export default function AISpeakingPictureComparisonPage() {
  const [pairIdx, setPairIdx] = useState(0)
  const [phase, setPhase] = useState<'prep' | 'recording' | 'done'>('prep')
  const [seconds, setSeconds] = useState(0)
  const [result, setResult] = useState<any>(null)
  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const pair = SAMPLE_PAIRS[pairIdx]

  const submit = useMutation({
    mutationFn: (blob: Blob) => {
      const fd = new FormData()
      fd.append('audio', blob, 'audio.webm')
      fd.append('meta', JSON.stringify({
        partType: 'picture_comparison',
        questionPrompt: pair.prompt,
        sessionId: 'picture-comparison',
      }))
      return speakingAPI.submit(fd)
    },
    onSuccess: (data) => { setResult(data); setPhase('done') },
  })

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      chunksRef.current = []
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data)
      recorder.onstop = () => {
        submit.mutate(new Blob(chunksRef.current, { type: 'audio/webm' }))
        stream.getTracks().forEach(t => t.stop())
      }
      recorder.start()
      mediaRef.current = recorder
      setPhase('recording')
      setSeconds(0)
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } catch {
      alert('Không thể truy cập microphone.')
    }
  }

  function stopRecording() {
    mediaRef.current?.stop()
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  function reset() {
    setPhase('prep'); setResult(null); setSeconds(0)
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  return (
    <AppLayout>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#b41340', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>compare</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b41340' }}>Speaking — Picture Comparison</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          So sánh <span style={{ color: '#004be2', fontStyle: 'italic' }}>hai hình ảnh</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Quan sát kỹ hai ảnh, sau đó mô tả và so sánh bằng tiếng Anh. AI sẽ đánh giá toàn diện.
        </p>
      </div>

      {/* Pair selector */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {SAMPLE_PAIRS.map((p, i) => (
          <button key={p.id} onClick={() => { setPairIdx(i); reset() }}
            style={{ padding: '0.5rem 1.25rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.8125rem', transition: 'all 0.2s', background: pairIdx === i ? '#004be2' : '#eef0ff', color: pairIdx === i ? 'white' : '#535b71' }}>
            {p.label}
          </button>
        ))}
      </div>

      {phase !== 'done' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {[pair.imageA, pair.imageB].map((src, i) => (
            <div key={i} style={{ borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', position: 'relative' }}>
              <img src={src} alt={`Image ${i + 1}`} style={{ width: '100%', height: '18rem', objectFit: 'cover', display: 'block' }} />
              <div style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', borderRadius: '9999px', padding: '0.375rem 1rem', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem' }}>
                Ảnh {i + 1}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Prompt card */}
      {phase !== 'done' && (
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem 2rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.25rem', fontVariationSettings: "'FILL' 1", flexShrink: 0, marginTop: '0.125rem' }}>help</span>
            <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#272e42', fontWeight: 500 }}>{pair.prompt}</p>
          </div>
        </div>
      )}

      {/* Recording controls */}
      {phase !== 'done' && (
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.05)', textAlign: 'center' }}>
          {phase === 'prep' && (
            <>
              <p style={{ color: '#535b71', marginBottom: '1.5rem', fontSize: '0.9375rem' }}>Chuẩn bị 30 giây, sau đó nhấn ghi âm và nói trong 60–90 giây.</p>
              <button onClick={startRecording}
                style={{ padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #b41340, #f74b6d)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(180,19,64,0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>mic</span>
                Bắt đầu ghi âm
              </button>
            </>
          )}
          {phase === 'recording' && (
            <>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.5rem', fontWeight: 900, color: '#b41340', marginBottom: '1rem' }}>{fmt(seconds)}</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '1.5rem', height: '3rem' }}>
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={i} style={{ width: '0.25rem', borderRadius: '9999px', background: '#b41340', height: `${8 + Math.random() * 28}px`, animation: `pulse-wave ${0.7 + (i % 4) * 0.15}s ease-in-out infinite alternate` }} />
                ))}
              </div>
              {submit.isPending ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', color: '#535b71' }}>
                  <div className="spinner" /> Đang phân tích...
                </div>
              ) : (
                <button onClick={stopRecording}
                  style={{ padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: '#272e42', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>stop</span>
                  Dừng & Gửi
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Result */}
      {phase === 'done' && result && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 22rem', gap: '1.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Scores */}
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
              <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '1.5rem' }}>Kết quả phân tích</H>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                {[
                  { label: 'Tổng điểm', value: result.content?.scoreOverall, color: '#004be2' },
                  { label: 'Phát âm', value: result.pronunciation?.pronunciationScore, color: '#b41340' },
                  { label: 'Từ vựng', value: result.content?.scoreVocabulary, color: '#8e00d5' },
                  { label: 'Ngữ pháp', value: result.content?.scoreGrammar, color: '#006571' },
                ].map(s => (
                  <div key={s.label} style={{ textAlign: 'center', padding: '1.25rem', background: `${s.color}08`, borderRadius: '1rem' }}>
                    <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: s.color }}>{s.value?.toFixed(1) ?? '—'}</H>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {result.transcript && (
              <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1rem' }}>Transcript</H>
                <p style={{ fontSize: '1rem', lineHeight: 1.85, color: '#272e42' }}>{result.transcript}</p>
              </div>
            )}
            {result.content?.feedback && (
              <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2rem' }}>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', color: '#8e00d5', marginBottom: '1rem' }}>Nhận xét AI Coach</H>
                <p style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#272e42' }}>{result.content.feedback}</p>
              </div>
            )}
            <button onClick={reset}
              style={{ alignSelf: 'flex-start', padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem' }}>
              Thử lại
            </button>
          </div>
          {/* Word scores */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)', alignSelf: 'start' }}>
            <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1.25rem' }}>Phân tích từng từ</H>
            {(result.pronunciation?.wordScores ?? []).slice(0, 15).map((w: any, i: number) => {
              const pct = Math.min(100, w.accuracyScore ?? 80)
              const color = pct >= 80 ? '#004be2' : pct >= 60 ? '#b45309' : '#b41340'
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.875rem', minWidth: '6rem' }}>{w.word}</span>
                  <div style={{ flex: 1, height: '0.375rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '9999px' }} />
                  </div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.8125rem', color, minWidth: '2.5rem', textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <style>{`@keyframes pulse-wave { from { height: 8px; opacity: 0.5; } to { height: 36px; opacity: 1; } }`}</style>
    </AppLayout>
  )
}
